import { useFormStore, useStoreState } from '@ariakit/react'
import { isEmpty } from 'lodash-es'
import * as React from 'react'
import { useDebounce } from 'react-use'

import { useStableCallback } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useTrackSearchResults } from '../../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useGoogleTagManager } from '../../../../../lib/hooks/useGoogleTagManager'
import { useSearchAutocomplete } from '../../../../../lib/hooks/useSearchAutocomplete'

function noOp() {}

export interface ExposedMethods {
  clearSearchResults(): void
}

export interface Args {
  focusOnMount: boolean
  initialSearchText: string
  isMobileSearch?: boolean
  onAutoComplete?: (hasResults: boolean) => void
  onClearFormClick?: () => void
  onClickSearchOverlayProduct?: () => void
  onFormSubmit: (searchText: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: React.MutableRefObject<any> | ((instance: unknown) => void) | null
  shouldDisplaySearchOverlay: boolean
}

export function useViewModel({
  focusOnMount,
  initialSearchText,
  isMobileSearch,
  onAutoComplete = noOp,
  onClickSearchOverlayProduct,
  onClearFormClick = noOp,
  onFormSubmit,
  ref,
  shouldDisplaySearchOverlay,
}: Args) {
  const {
    error: searchError,
    fetchSearchAutocompleteResults,
    loading: loadingSearchAutocompleteResults,
    products: searchProducts,
    suggestions: searchSuggestions,
    noResult,
  } = useSearchAutocomplete()

  const { region } = useLocalization()
  const { search: searchCopy } = useTranslation()
  const { pushSearchSubmitted } = useGoogleTagManager()
  const { trackSearchResults } = useTrackSearchResults()

  // We don’t want to show autocomplete results if the input has not been edited:
  const [inputEdited, setInputEdited] = React.useState<boolean>(false)
  const hasError = !!searchError
  const [hideSearchResults, setHideSearchResults] = React.useState<boolean>(false)

  // If we have new initial search text we consider the input clean again:
  React.useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setInputEdited(false)
  }, [initialSearchText])

  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (focusOnMount && inputRef.current && shouldDisplaySearchOverlay) {
      inputRef.current.focus()
    }
  }, [focusOnMount, inputRef, shouldDisplaySearchOverlay])

  const form = useFormStore({
    defaultValues: { searchText: initialSearchText },
  })

  const searchText = useStoreState(form, state => state.values.searchText)
  const searchTextError = useStoreState(form, () => form.getError('searchText'))
  const searchTextTouched = useStoreState(form, () => form.getFieldTouched('searchText'))

  // sync when initialSearchText changes externally
  React.useEffect(() => {
    form.setValue('searchText', initialSearchText)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchText])

  form.useValidate(state => {
    if (!state.values.searchText || !state.values.searchText.trim()) {
      form.setError('searchText', searchCopy.inputRequiredMessage)
    }
  })

  form.useSubmit(state => {
    onFormSubmit(state.values.searchText)
    pushSearchSubmitted(state.values.searchText)
  })

  const showSearchResults = !hideSearchResults && !hasError
  const products = React.useMemo(() => {
    return showSearchResults
      ? searchProducts.filter(searchProduct => region.id === 'US' || !searchProduct.isGiftCard)
      : []
  }, [showSearchResults, searchProducts, region.id])

  const suggestions = showSearchResults ? searchSuggestions : []

  const isInputInvalid: boolean = !!searchTextTouched && !!searchTextError

  const clearSearchResults = React.useCallback(() => {
    setHideSearchResults(true)
  }, [setHideSearchResults])

  const resetForm = React.useCallback(() => {
    form.setValue('searchText', '')
    clearSearchResults()
    onClearFormClick()
    inputRef.current?.focus()
  }, [clearSearchResults, form, onClearFormClick])

  const fetchSearchResults = React.useCallback(() => {
    try {
      const query: string = searchText
      if (!query || !query.trim() || !inputEdited) return

      fetchSearchAutocompleteResults({
        variables: {
          query,
          first: 6,
        },
      })

      setHideSearchResults(false)
      onAutoComplete(Boolean(products.length || suggestions.length))
    } catch (_error) {}
  }, [
    searchText,
    inputEdited,
    fetchSearchAutocompleteResults,
    onAutoComplete,
    products.length,
    suggestions.length,
  ])

  const [isDebounceReady, cancelInputDebounce] = useDebounce(fetchSearchResults, 500, [searchText])

  const trackingRefHolder = React.useRef('')
  React.useEffect(() => {
    const query = searchText.trim()
    if (
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !showSearchResults ||
      loadingSearchAutocompleteResults ||
      isEmpty(query) ||
      isDebounceReady() === false
    ) {
      return
    }

    if (trackingRefHolder.current !== query) {
      trackSearchResults({
        id: `header_`,
        query,
        searchType: 'header',
        topResults: products.map(product => {
          return {
            handle: product.handle,
            color: product.colorKey ?? '',
            id: product.id,
            soldOut: product.soldOut,
            shopifyId: product.defaultShopifyProductId,
          }
        }),
        totalResults: products.length,
      })
      trackingRefHolder.current = query
    }
  }, [
    trackSearchResults,
    products,
    inputEdited,
    searchText,
    showSearchResults,
    loadingSearchAutocompleteResults,
    isDebounceReady,
  ])

  React.useImperativeHandle(ref, () => ({
    clearSearchResults() {
      cancelInputDebounce()
      clearSearchResults()
    },
  }))

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      cancelInputDebounce()
      clearSearchResults()
      form.submit()
    },
    [cancelInputDebounce, clearSearchResults, form]
  )

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputEdited(true)
      form.setValue('searchText', event.target.value)
    },
    [form]
  )

  const handleClickSearchOverlayProduct = useStableCallback(() => {
    if (onClickSearchOverlayProduct) {
      onClickSearchOverlayProduct()
    }
    if (isMobileSearch) {
      resetForm()
    }
  })

  const showNoResultsMessage: boolean | undefined =
    noResult && searchText.length > 0 && shouldDisplaySearchOverlay

  return {
    autoCompleteResults: products,
    handleFormChange: handleChange,
    handleSubmit,
    hasError,
    inputPlaceholder: searchCopy.inputPlaceholder,
    inputRef,
    isInputInvalid,
    onClearFormClick,
    onClickSearchOverlayProduct: handleClickSearchOverlayProduct,
    popularSearchResults: suggestions,
    popularSearchTitle: searchCopy.popularSearchTitle,
    resetForm,
    searchButtonAriaLabelClear: searchCopy.searchButtonAriaLabelClear,
    searchButtonAriaLabelSubmit: searchCopy.searchButtonAriaLabelSubmit,
    searchTextError,
    searchTextValue: searchText,
    showNoResultsMessage,
  }
}
